
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Report } from '@/types/report';
import { DateTime } from 'luxon';
import { formatDate, formatTime } from '@/utils/reportFormatters';

const ITEMS_PER_PAGE = 10;

export const useUserReports = (userEmail?: string) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { toast } = useToast();

  const fetchReports = async () => {
    if (!userEmail) return;
    
    setLoading(true);
    try {
      // Get total count first
      const { count, error: countError } = await supabase
        .from('student_development_reports')
        .select('*', { count: 'exact', head: true })
        .eq('submitter_email', userEmail);
        
      if (countError) throw countError;
      
      if (count !== null) {
        setTotalPages(Math.ceil(count / ITEMS_PER_PAGE));
      }
      
      // Then fetch the paginated data - removed pinned sorting, only sort by created_at
      const { data, error } = await supabase
        .from('student_development_reports')
        .select('id, created_at, student_nickname, student_full_name, subject, teaching_date, status, start_date, end_date, teaching_time_slot, rejection_reason, pinned')
        .eq('submitter_email', userEmail)
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

      if (error) {
        throw error;
      }

      // Make sure the data is valid and has expected properties
      if (Array.isArray(data)) {
        const validReports = data.filter(item => 
          typeof item === 'object' && 
          item !== null && 
          'id' in item
        ) as Report[];
        
        setReports(validReports);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        title: 'ไม่สามารถโหลดรายงานได้',
        description: 'โปรดลองอีกครั้งในภายหลัง',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [userEmail, currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return {
    reports,
    loading,
    currentPage,
    totalPages,
    handlePageChange,
    formatDate,
    formatTime
  };
};
